import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { Button } from '@mui/material';
import { flexRender, Table } from '@tanstack/react-table';
import React from 'react';
import { CircularLoading } from '../shared/circular-loading';
import { AgisticsTablePaging } from '../shared/table/components/agistics-table-paging';
import { ReactTableColumns, ReportRunOptions } from './report-types';
import './report.scss';

interface ReportBaseProps {
    table: Table<ReactTableColumns>;
    hasData: boolean;
    isLoading: boolean;
    runReport: (r: ReportRunOptions) => void;
    confined?: boolean;
    additionalCsvButton?: React.ReactNode
}

export const ReportBase = (props: ReportBaseProps) => {
    const { table, hasData, isLoading, runReport, confined, additionalCsvButton } = props;

    return (
        isLoading ? <CircularLoading /> :
        !hasData ? <></> :
        <div style={{ width: confined ? '60vw' : 'inherit' }}>
            <ReportPagingHeaderFooter table={table} onClick={runReport} additionalCsvButton={additionalCsvButton} />
            <table className='report-base'>
                <thead>
                    {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map(header => {
                                return (
                                    <th
                                        key={header.id}
                                        colSpan={header.colSpan}
                                        onClick={() => {
                                            if (header.column.getCanSort()) {
                                                header.column.toggleSorting();
                                            }
                                        }}
                                        style={{
                                            position: 'relative',
                                            width: header.getSize(),
                                        }}
                                        className={header.column.getCanSort() ? 'cursor-pointer' : ''}
                                    >
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(
                                            header.column.columnDef.header,
                                            header.getContext()
                                        )}
                                        {{
                                            asc: <span>&#x25B2;</span>,
                                            desc: <span>&#x25BC;</span>,
                                        }[header.column.getIsSorted() as string] ?? null}
                                    {header.column.getCanResize() && (
                                        <div
                                            onMouseDown={header.getResizeHandler()}
                                            onTouchStart={header.getResizeHandler()}
                                            className={`resizer ${
                                                header.column.getIsResizing() ? 'isResizing' : ''
                                            }`}
                                        ></div>
                                    )}
                                    </th>
                                )
                            })}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {table.getRowModel().rows.map(row => {
                        return (
                            <tr key={row.id} className='table-row'>
                                {row.getVisibleCells().map(cell => {
                                    // @ts-ignore
                                    const isNumber = !isNaN(cell.getValue());
                                    const isBool = typeof(cell.getValue()) === 'boolean';
                                    return (
                                        <td key={cell.id}
                                            className={`table-data ${isNumber && 'table-data-right'} ${isBool && 'table-data-center'}`}
                                            style={{ width: cell.column.getSize() }}>
                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext()
                                            )}
                                        </td>
                                    )
                                })}
                            </tr>
                        )
                    })}
                </tbody>
            </table>
            <ReportPagingHeaderFooter table={table} onClick={runReport} additionalCsvButton={additionalCsvButton} />
        </div>
    );
};

const ReportPagingHeaderFooter = ({table, onClick, additionalCsvButton}: {
    table: Table<ReactTableColumns>;
    onClick: (r: ReportRunOptions) => void;
    additionalCsvButton?: React.ReactNode;
}) => (<AgisticsTablePaging table={table}>
        <Button
            size='small'
            variant='outlined'
            startIcon={<CloudDownloadIcon />}
            style={{ marginLeft: '10px' }}
            onClick={() => onClick({ csv: true })}
            disabled={!table.getRowCount()}
        >
            CSV
        </Button>
        {additionalCsvButton}
    </AgisticsTablePaging>
);